import React, { CSSProperties, forwardRef, useImperativeHandle, useRef } from 'react';
import { ViewportList, ViewportListRef } from 'react-viewport-list';
import styled from 'styled-components';

import { SubscriptionTopic } from '../../../../../../viewer/messaging/pubsub';
import { useSubscribe } from '../../../../../hooks/use-subscribe';

export interface VirtualAssetListProps<ItemT> {
  items: ItemT[];
  assetType: string;
  getAssetId: (item: ItemT) => string;
  overscan?: number;
  children: (item: ItemT, index: number) => React.ReactNode;
  onScrolledIntoIndex?: (item: ItemT) => void;

  maxHeight?: CSSProperties['maxHeight'];
}

/**
 * A reusable viewport-virtualised list that scrolls to the item that was
 * selected somewhere else in the app (through the NewSelection pub-sub).
 *
 * It forwards the underlying ViewportListRef so parent components can still
 * call scrollToIndex / scrollToTop directly if they want to.
 */
function Inner<ItemT>(
  {
    items,
    assetType,
    getAssetId,
    overscan = 50,
    children,
    maxHeight,
    onScrolledIntoIndex,
  }: VirtualAssetListProps<ItemT>,
  ref: React.Ref<ViewportListRef>,
) {
  const listRef = useRef<ViewportListRef>(null);
  const viewportRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => listRef.current!, []);

  /* Auto-scroll when another part of the app selects something */
  useSubscribe(SubscriptionTopic.AssetSelectedInViewer, (msg) => {
    if (msg.assetType !== assetType) {
      return;
    }
    const idx = items.findIndex((it) => getAssetId(it) === msg.assetId);
    if (idx !== -1) {
      queueMicrotask(() => {
        listRef.current?.scrollToIndex({ index: idx });
        onScrolledIntoIndex?.(items[idx]);
      });
    }
  });

  return (
    <ScrollContainer ref={viewportRef} {...{ maxHeight }}>
      <ViewportList items={items} overscan={overscan} ref={listRef} viewportRef={viewportRef}>
        {children}
      </ViewportList>
    </ScrollContainer>
  );
}

// Container that does the actual scrolling
const ScrollContainer = styled.div<{ maxHeight?: CSSProperties['maxHeight'] }>`
  max-height: ${(props) => props.maxHeight ?? 'unset'};
  overflow-y: auto;
`;

Inner.displayName = 'VirtualAssetList';

export const VirtualAssetList = forwardRef(Inner) as <T>(
  props: VirtualAssetListProps<T> & React.RefAttributes<ViewportListRef>,
) => JSX.Element;
